// src/modules/units/tyres/views/reports/tyrereports.view.js
//
// Pure markup for the fleet report. Takes the computed report object and returns
// HTML; no fetching or DOM side effects, so it can be rendered and checked in
// isolation.

import { esc, formatKm, formatMoney, formatCount } from '../../shared/tyre-format.js';
import { tyreStatusLabel } from '../../shared/tyre-status.js';
import { REPORT_THRESHOLDS, remainingTreadPct } from './tyre-reports-data.js';

const money = (v) => `R ${formatMoney(v || 0)}`;

function kpi(value, label) {
  return `<div class="tm-kpi"><span class="tm-kpi-value">${value}</span><span class="tm-kpi-label">${esc(label)}</span></div>`;
}

function pane(title, inner, extraClass = '') {
  return `<section class="tm-pane ${extraClass}"><h3 class="tm-pane-title">${esc(title)}</h3>${inner}</section>`;
}

function kvRows(pairs) {
  return `<dl class="tm-kv">${pairs.map(([l, v]) => `<div class="tm-kv-row"><dt>${esc(l)}</dt><dd>${v}</dd></div>`).join('')}</dl>`;
}

function statusCard(byStatus) {
  const rows = byStatus.map((s) => `
    <div class="tm-statrow">
      <span class="tm-statlabel"><span class="tm-badge tm-tone-${s.tone}">${esc(s.label)}</span></span>
      <span class="tm-statbar"><span class="tm-statbar-fill tm-tone-${s.tone}" style="width:${Math.max(2, s.pct).toFixed(1)}%"></span></span>
      <span class="tm-statcount">${s.count} <span class="tm-muted">(${Math.round(s.pct)}%)</span></span>
    </div>`).join('');
  return pane('By status', `<div class="tm-statlist">${rows}</div>`);
}

function costCard(cost) {
  const totals = kvRows([
    ['Purchase total', money(cost.totalPurchase)],
    ['Service total', money(cost.totalService)],
    ['Avg purchase / tyre', money(cost.avgPurchase)]
  ]);
  const suppliers = cost.bySupplier.length
    ? `<h4 class="tm-subhead">Top suppliers by spend</h4>` + kvRows(
        cost.bySupplier.slice(0, 5).map((s) => [s.supplier, `${money(s.total)} <span class="tm-muted">(${s.count})</span>`]))
    : '';
  return pane('Cost', totals + suppliers);
}

function segBar(label, segments) {
  const total = segments.reduce((a, s) => a + (Number(s.value) || 0), 0);
  if (!total) return '';
  const track = segments.map((s) => {
    const pct = ((Number(s.value) || 0) / total) * 100;
    return pct > 0 ? `<span class="tm-kmseg tm-tone-${s.tone}" style="width:${pct.toFixed(2)}%"></span>` : '';
  }).join('');
  const legend = segments.map((s) => {
    const pct = Math.round(((Number(s.value) || 0) / total) * 100);
    return `<span class="tm-kmleg"><span class="tm-kmdot tm-tone-${s.tone}"></span>${esc(s.label)} <b>${esc(formatKm(s.value))}</b> <span class="tm-muted">(${pct}%)</span></span>`;
  }).join('');
  return `<div class="tm-kmbar"><div class="tm-kmbar-label">${esc(label)}</div><div class="tm-kmtrack">${track}</div><div class="tm-kmlegend">${legend}</div></div>`;
}

function inflationCard(distance) {
  const byPressure = segBar('Distance by pressure', [
    { label: 'Over-inflated', value: distance.pressure.over, tone: 'presshigh' },
    { label: 'Normal', value: distance.pressure.normal, tone: 'normal' },
    { label: 'Under-inflated', value: distance.pressure.under, tone: 'presslow' }
  ]);
  const byTemp = segBar('Distance by temperature', [
    { label: 'High', value: distance.temperature.high, tone: 'temphigh' },
    { label: 'Normal', value: distance.temperature.normal, tone: 'normal' }
  ]);
  const inner = (byPressure || byTemp)
    ? `<div class="tm-km-total"><span class="tm-km-total-value">${esc(formatKm(distance.totalKm))}</span><span class="tm-km-total-label">fleet distance</span></div>${byPressure}${byTemp}`
    : `<div class="tm-empty-pane"><p>No distance recorded across the fleet yet.</p></div>`;
  return pane('Inflation health', inner);
}

function attentionCard(report) {
  const { attention, attentionCounts } = report;
  if (!attention.length) {
    return pane('Attention needed', `<div class="tm-empty-pane"><p>No tyres need attention.</p><p class="tm-muted">Nothing below ${REPORT_THRESHOLDS.treadWatchPct}% tread or over ${REPORT_THRESHOLDS.underinflationPct}% under-inflated.</p></div>`, 'tm-pane-full');
  }
  const CAP = 40;
  const shown = attention.slice(0, CAP);
  const summary = `<div class="tm-attn-summary">
    ${attentionCounts.critical ? `<span class="tm-attn-pill tm-attn-critical">${attentionCounts.critical} critical</span>` : ''}
    ${attentionCounts.watch ? `<span class="tm-attn-pill tm-attn-watch">${attentionCounts.watch} to watch</span>` : ''}
  </div>`;
  const rows = shown.map((a) => {
    const t = a.tyre;
    const pct = remainingTreadPct(t);
    const flags = a.reasons.map((r) => `<span class="tm-flag tm-flag-${r.level}">${esc(r.text)}</span>`).join(' ');
    return `<tr>
      <td class="tm-col-id">${esc(t.externalTyreId || '—')}</td>
      <td>${esc(tyreStatusLabel(t))}</td>
      <td class="tm-col-num">${pct == null ? '—' : Math.round(pct) + '%'}</td>
      <td class="tm-col-num">${esc(formatKm(t.tyreKm))}</td>
      <td>${flags}</td>
    </tr>`;
  }).join('');
  const more = attention.length > CAP ? `<p class="tm-muted tm-attn-more">+ ${attention.length - CAP} more — export the CSV for the full list.</p>` : '';
  const table = `
    <div class="tm-table-wrap">
      <table class="tm-table">
        <thead><tr><th class="tm-col-id">Tyre ID</th><th>Status</th><th class="tm-col-num">Tread</th><th class="tm-col-num">Distance</th><th>Flags</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </div>${more}`;
  return pane('Attention needed', summary + table, 'tm-pane-full');
}

export function reportHTML(report) {
  const kpis = `<div class="tm-kpis">
    ${kpi(formatCount(report.total), 'Tyres')}
    ${kpi(formatCount(report.fitted), 'Fitted')}
    ${kpi(formatCount(report.inStock), 'In stock')}
    ${kpi(money(report.cost.totalPurchase), 'Fleet value')}
    ${kpi(formatKm(report.distance.totalKm), 'Total distance')}
  </div>`;

  return `
    <div class="tm-reports">
      ${kpis}
      <div class="tm-panes">
        ${statusCard(report.byStatus)}
        ${costCard(report.cost)}
        ${inflationCard(report.distance)}
        ${applicationCard(report.byApplication)}
      </div>
      ${attentionCard(report)}
    </div>`;
}

function applicationCard(byApplication) {
  if (!byApplication.length) return pane('By application', `<div class="tm-empty-pane"><p>No application data.</p></div>`);
  const rows = kvRows(byApplication.map((a) => [a.application, `${a.count}`]));
  return pane('By application', rows);
}

// Loading / error shells (rendered by the orchestrator around the fetch).
export function reportsLoadingHTML() {
  return `<div class="tm-reports">
    <div class="tm-kpis">${Array.from({ length: 5 }, () => '<div class="tm-kpi"><span class="tm-skel" style="height:22px;width:60%;margin:0 auto 8px"></span><span class="tm-skel" style="height:10px;width:40%;margin:0 auto"></span></div>').join('')}</div>
    <div class="tm-tl-state tm-muted">Gathering fleet data…</div>
  </div>`;
}

export function reportsErrorHTML(message) {
  return `<div class="tm-reports"><div class="tm-state" style="padding:40px;justify-content:center">
    <span class="tm-state-msg">${esc(message || 'Could not build the report.')}</span>
    <button type="button" class="tm-retry" data-retry>Retry</button>
  </div></div>`;
}
